import { supabase } from "@/integrations/supabase/client";
import type {
  AiCreditTier,
  AiCreditsInfo,
  AiUsageStat,
  AiCreditSummary,
} from "@/types/aiCredits";

// ---- Credit Tier CRUD ----

export async function fetchCreditTiers(): Promise<AiCreditTier[]> {
  const { data, error } = await supabase
    .from("ai_credit_tiers" as any)
    .select("*")
    .order("id");

  if (error) throw error;
  return (data || []) as AiCreditTier[];
}

export async function updateCreditTier(
  id: number,
  updates: Partial<Pick<AiCreditTier, "credits_per_period" | "reset_period_hours" | "is_active">>
): Promise<AiCreditTier> {
  const { data, error } = await supabase
    .from("ai_credit_tiers" as any)
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data as AiCreditTier;
}

// ---- Credit Check (for current user) ----

export async function checkMyCredits(): Promise<AiCreditsInfo> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) throw new Error("Not authenticated");

  const { data, error } = await supabase.rpc("check_ai_credits", {
    p_user_id: user.id,
  });

  if (error) throw error;
  return data as AiCreditsInfo;
}

// ---- Admin Analytics ----

export async function fetchUsageStats(
  hours: number = 24
): Promise<AiUsageStat[]> {
  const { data, error } = await supabase.rpc("get_ai_usage_stats", {
    p_hours: hours,
  });

  if (error) throw error;
  return (data || []) as AiUsageStat[];
}

export async function fetchCreditSummary(
  hours: number = 24
): Promise<AiCreditSummary> {
  const { data, error } = await supabase.rpc("get_ai_credit_summary", {
    p_hours: hours,
  });

  if (error) throw error;
  return (data || {
    total_credits_used: 0,
    unique_users: 0,
    total_interactions: 0,
    avg_credits_per_user: 0,
  }) as AiCreditSummary;
}
